<script setup lang="ts">
/**
 * Agents, in four tabs (?tab=): your and the shared agents with the night
 * shift; what ships built in (agents, the code agent, side sessions);
 * templates to start from; recent runs. Run one with a goal.
 */
import { Bot, CalendarClock, Code, LayoutTemplate, ListChecks, MoonStar, Play, Plus, Share2, Sparkles } from "@lucide/vue";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import { isActive, startAgentRun, useAgentRuns, useAgents, type Agent } from "@/api/agents";
import { describeCron, NIGHT_TEMPLATE, runTitle, type AgentsCatalog } from "@/components/agents/agentMeta";
import BuiltinsCard from "@/components/agents/BuiltinsCard.vue";
import CodeRunInfo from "@/components/agents/CodeRunInfo.vue";
import NightShiftCard from "@/components/agents/NightShiftCard.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs, { type PageTabItem } from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { formatUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Agents", requires: { capability: "agents.read" }, layout: "wide" } });

const router = useRouter();
const { can } = useSession();
const agents = useAgents();
/** The full catalogue (built-ins with their purpose, side sessions), typed from the schema. */
const catalog = computed(() => agents.data.value as AgentsCatalog | undefined);
const runs = useAgentRuns(() => ({ limit: 20 }));

const TABS = ["agents", "builtin", "templates", "runs"] as const;
const tabQuery = useRouteQuery("tab", "agents");
const tab = computed({
  get: () => ((TABS as readonly string[]).includes(tabQuery.value) ? tabQuery.value : "agents"),
  set: (v: string) => (tabQuery.value = v),
});
const activeRuns = computed(() => runs.data.value?.filter(isActive).length ?? 0);
const tabs = computed<PageTabItem[]>(() => [
  { id: "agents", label: "Agents", icon: Bot, badge: agents.data.value?.items.length },
  { id: "builtin", label: "Built in", icon: Sparkles },
  { id: "templates", label: "Templates", icon: LayoutTemplate, badge: catalog.value?.templates.length },
  { id: "runs", label: "Runs", icon: ListChecks, badge: activeRuns.value ? `${activeRuns.value} running` : undefined },
]);

const runFor = ref<{ ref: string; title: string; maxMinutes: number; code: boolean; todoWrites: boolean } | null>(null);
const runOpen = computed({ get: () => Boolean(runFor.value), set: (v) => !v && (runFor.value = null) });
const goal = ref("");
const minutes = ref("");
const busy = ref(false);
const error = ref<unknown>(null);

function openRun(ref: string, title: string, maxMinutes: number, defaultGoal = "", code = false, todoWrites = false) {
  runFor.value = { ref, title, maxMinutes, code, todoWrites };
  goal.value = defaultGoal;
  minutes.value = "";
  error.value = null;
}
const runAgent = (a: Agent) => openRun(a.name, a.title, a.maxMinutes, a.scheduleInput ?? "", a.kind === "code", a.tools.includes("todo_change"));

async function start() {
  if (!runFor.value) return;
  busy.value = true;
  error.value = null;
  try {
    const run = await startAgentRun(runFor.value.ref, goal.value.trim(), minutes.value ? Number(minutes.value) : undefined);
    toast.success(`${runFor.value.title} started.`);
    runFor.value = null;
    await router.push(`/agents/runs/${run.id}`);
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

const mine = computed(() => (agents.data.value?.items ?? []).filter((a) => a.visibility === "private"));
const shared = computed(() => (agents.data.value?.items ?? []).filter((a) => a.visibility === "shared"));
const groups = computed(() => [
  { key: "shared", title: "Shared agents", icon: Share2, items: shared.value, empty: "No shared agents yet", hint: "Agents admins share agents; everyone runs them as themselves." },
  { key: "mine", title: "Your agents", icon: Bot, items: mine.value, empty: "No agents of your own yet", hint: "Start from a template: it copies into your agents, where you change everything." },
]);
const canRun = computed(() => can("ai.exec"));
const shownRuns = computed(() => (tab.value === "runs" ? runs.data.value : runs.data.value?.slice(0, 5)) ?? []);
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader
      title="Agents"
      description="Agents work with smake-mcp's tools as the person they run for — on demand, on a schedule, from the todo list or in the night shift. Loop agents gather evidence and propose changes; code agents run Claude Code in a codespace, commit on their own branch and open draft PRs."
    >
      <template #actions>
        <Button v-if="can('agents.write')" @click="router.push('/agents/new')"><Plus /> New agent</Button>
      </template>
      <template #tabs>
        <PageTabs v-model="tab" :items="tabs" label="Agents" id-prefix="agents-tab" />
      </template>
    </PageHeader>

    <ListSkeleton v-if="agents.isPending.value && tab !== 'runs'" :rows="4" />
    <ErrorState v-else-if="agents.error.value && tab !== 'runs'" :error="agents.error.value" @retry="agents.refetch()" />

    <!-- Agents: shared and your own, the night shift -->
    <div v-else-if="tab === 'agents' && catalog" role="tabpanel" aria-labelledby="agents-tab-agents" class="flex flex-col gap-6">
      <div class="grid gap-6 lg:grid-cols-2">
        <SectionCard v-for="group in groups" :key="group.key" :title="group.title" :icon="group.icon">
          <template #badge><span class="text-xs text-muted-foreground tabular-nums">{{ group.items.length || "" }}</span></template>
          <EmptyState v-if="!group.items.length" :icon="group.icon" :title="group.empty" :description="group.hint" compact>
            <Button v-if="group.key === 'mine' && can('agents.write')" size="sm" variant="outline" @click="tab = 'templates'"><LayoutTemplate /> Browse templates</Button>
          </EmptyState>
          <ul v-else class="-mx-2 flex flex-col">
            <li v-for="a in group.items" :key="a.id" class="flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-state-hover">
              <span
                :class="cn('mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg', a.kind === 'code' ? 'bg-info-soft text-info' : a.template === NIGHT_TEMPLATE ? 'bg-night/12 text-night' : 'bg-muted text-muted-foreground')"
                aria-hidden="true"
              >
                <Code v-if="a.kind === 'code'" class="size-4" /><MoonStar v-else-if="a.template === NIGHT_TEMPLATE" class="size-4" /><Bot v-else class="size-4" />
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-1.5">
                  <RouterLink :to="`/agents/${a.name}`" class="font-medium underline-offset-2 hover:underline">{{ a.title }}</RouterLink>
                  <Badge v-if="a.kind === 'code'" variant="info" class="font-normal" :title="a.repo ? `Claude Code on ${a.repo}` : 'Claude Code'"><Code /> {{ a.repo ? a.repo.split("/").pop() : "code" }}</Badge>
                  <Badge v-if="a.schedule" variant="outline" class="font-normal" :title="`UTC cron ${a.schedule}`"><CalendarClock /> {{ describeCron(a.schedule) }}</Badge>
                  <Badge v-if="a.autoTodos" variant="outline" class="font-normal" title="Works on the todos assigned to it"><ListChecks /> todos</Badge>
                  <Badge v-if="!a.enabled" variant="muted">off</Badge>
                </div>
                <p class="mt-0.5 line-clamp-2 text-xs text-pretty text-muted-foreground">{{ a.description || a.instructions.slice(0, 160) }}</p>
              </div>
              <Button v-if="canRun && a.enabled" size="sm" variant="outline" class="shrink-0" :aria-label="`Run ${a.title}`" @click="runAgent(a)"><Play /> Run</Button>
            </li>
          </ul>
        </SectionCard>
      </div>

      <NightShiftCard :templates="catalog.templates" />
    </div>

    <!-- Built in: agents, the code agent, side sessions -->
    <div v-else-if="tab === 'builtin' && catalog" role="tabpanel" aria-labelledby="agents-tab-builtin">
      <BuiltinsCard :catalog="catalog" :runs="runs.data.value" :can-run="canRun" @run="(name, title, max) => openRun(name, title, max)" />
    </div>

    <!-- Templates -->
    <div v-else-if="tab === 'templates' && catalog" role="tabpanel" aria-labelledby="agents-tab-templates" class="flex flex-col gap-4">
      <p class="text-sm text-muted-foreground">Starting points: using one copies it into your agents, where you change everything.</p>
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        <Card v-for="t in catalog.templates" :key="t.id" padding="sm" :class="cn(t.id === NIGHT_TEMPLATE && 'border-night/40 bg-night/[0.04]')">
          <div class="flex items-start gap-3">
            <span
              :class="cn('grid size-8 shrink-0 place-items-center rounded-lg', t.kind === 'code' ? 'bg-info-soft text-info' : t.id === NIGHT_TEMPLATE ? 'bg-night/12 text-night' : 'bg-muted text-muted-foreground')"
              aria-hidden="true"
            >
              <Code v-if="t.kind === 'code'" class="size-4" /><MoonStar v-else-if="t.id === NIGHT_TEMPLATE" class="size-4" /><LayoutTemplate v-else class="size-4" />
            </span>
            <div class="min-w-0 flex-1">
              <p class="card-title">{{ t.title }}</p>
              <div class="mt-1 flex flex-wrap gap-1 empty:hidden">
                <Badge v-if="t.kind === 'code'" variant="info" class="font-normal"><Code /> code</Badge>
                <Badge v-if="t.id === NIGHT_TEMPLATE" variant="outline" class="font-normal"><MoonStar /> night shift</Badge>
                <Badge v-if="t.schedule" variant="outline" class="font-normal" :title="`UTC cron ${t.schedule}`"><CalendarClock /> {{ describeCron(t.schedule) }}</Badge>
              </div>
            </div>
          </div>
          <p class="flex-1 text-xs text-pretty text-muted-foreground">{{ t.description }}</p>
          <Button v-if="can('agents.write')" size="sm" variant="outline" class="self-start" @click="router.push(`/agents/new?template=${t.id}`)"><Plus /> Use</Button>
        </Card>
      </div>
    </div>

    <!-- Recent runs (the latest five under the agents) -->
    <SectionCard
      v-if="tab === 'runs' || (tab === 'agents' && catalog && runs.data.value?.length)"
      :role="tab === 'runs' ? 'tabpanel' : undefined"
      :aria-labelledby="tab === 'runs' ? 'agents-tab-runs' : undefined"
      :title="tab === 'runs' ? 'Recent runs' : 'Latest runs'"
      :icon="ListChecks"
      :to="tab === 'runs' ? '/agents/runs' : '/agents?tab=runs'"
      :link-label="tab === 'runs' ? 'All runs' : 'Recent runs'"
    >
      <ListSkeleton v-if="runs.isPending.value" :rows="4" />
      <ErrorState v-else-if="runs.error.value" compact :error="runs.error.value" @retry="runs.refetch()" />
      <EmptyState v-else-if="!runs.data.value?.length" :icon="ListChecks" title="No runs yet" description="Runs show up here with their status, cost and — for code agents — the branch and pull request." compact />
      <ul v-else class="-mx-2 flex flex-col">
        <li v-for="r in shownRuns" :key="r.id" class="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-state-hover">
          <StatusBadge :status="r.status" />
          <RouterLink :to="`/agents/runs/${r.id}`" class="font-medium underline-offset-2 hover:underline">{{ runTitle(r, catalog) }}</RouterLink>
          <span class="min-w-0 flex-1 basis-40 truncate text-muted-foreground" :title="r.goal">{{ r.goal }}</span>
          <CodeRunInfo :run="r" />
          <Badge variant="outline" class="font-normal">{{ r.trigger }}</Badge>
          <span class="w-14 text-right text-xs tabular-nums text-muted-foreground">{{ isActive(r) ? "…" : formatUsd(r.costUsd) }}</span>
          <RelativeTime :value="r.createdAt" class="w-24 text-right text-xs text-muted-foreground" />
        </li>
      </ul>
    </SectionCard>

    <Dialog v-model:open="runOpen">
      <DialogContent class="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Run {{ runFor?.title }}</DialogTitle>
          <DialogDescription v-if="runFor?.code">It runs as you, with Claude Code in its codespace: it commits on its own branch and, when its instructions say so, pushes it and opens a draft PR. It never merges.</DialogDescription>
          <DialogDescription v-else-if="runFor?.todoWrites">It runs as you: your permissions and AI budget. It updates todos itself (audited); other changes come back as proposals for you.</DialogDescription>
          <DialogDescription v-else>It runs as you: your permissions and AI budget. Changes it would make come back as proposals for you.</DialogDescription>
        </DialogHeader>
        <form id="run-agent" class="flex flex-col gap-3" @submit.prevent="start">
          <div class="flex flex-col gap-1.5">
            <Label for="run-goal">Goal</Label>
            <Textarea id="run-goal" v-model="goal" rows="5" required placeholder="What it should find out or do, with every detail you know." />
          </div>
          <div class="flex flex-col gap-1.5 sm:w-48">
            <Label for="run-min">Minutes (max {{ runFor?.maxMinutes }})</Label>
            <Input id="run-min" v-model="minutes" type="number" min="1" :max="runFor?.maxMinutes" placeholder="default" />
          </div>
          <FormError :error="error" />
        </form>
        <DialogFooter>
          <Button variant="ghost" @click="runFor = null">Cancel</Button>
          <Button type="submit" form="run-agent" :loading="busy" :disabled="goal.trim().length < 5"><Play /> Start</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
